// pages/CollegePage.jsx
import React from 'react';
import { Badge } from 'react-bootstrap';
import {
  FaUniversity, FaEnvelope, FaPhone, FaMapMarkerAlt, FaIdCard, FaGlobe
} from 'react-icons/fa';
import GenericCrudPage from '../components/GenericCrudPage';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Validation for college form
const validateCollegeForm = (form) => {
  const errors = {};
  if (!form.name.trim()) errors.name = "College name is required";
  if (form.name.length > 100) errors.name = "College name must be less than 100 characters";
  if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = "Please enter a valid email address";
  if (form.telephone && !/^[0-9]{10}$/.test(form.telephone.replace(/\s/g, ""))) errors.telephone = "Telephone must be exactly 10 digits";
  if (form.fax && !/^[0-9]{10}$/.test(form.fax.replace(/\s/g, ""))) errors.fax = "Fax must be exactly 10 digits";
  if (!form.place.trim()) errors.place = "Place is required";
  if (!form.identifier.trim()) errors.identifier = "Identifier is required";
  return errors;
};

// Configuration for College CRUD operations
const collegeConfig = {
  baseUrl: BASE_URL,
  endpoints: {
    fetch: '/master/getColleges',
    save: '/master/saveColleges',
    update: '/master/updateColleges',
    delete: '/master/deleteCollege'
  },
  validateForm: validateCollegeForm,
  initialFormState: {
    name: "", email: "", telephone: "", fax: "", place: "", 
    address: "", identifier: "", isActive: 1, user: "", localLangName: ""
  },
  entityName: 'College'
};

// Form fields configuration
const formFields = [
  { name: "name", label: "College Name", icon: FaUniversity, required: true },
  { name: "localLangName", label: "Local Language Name", icon: FaGlobe },
  { name: "email", label: "Email Address", icon: FaEnvelope, type: "email" },
  { name: "telephone", label: "Telephone", icon: FaPhone },
  { name: "fax", label: "Fax Number", icon: FaPhone },
  { name: "place", label: "Place/City", icon: FaMapMarkerAlt, required: true },
  { name: "identifier", label: "Identifier Code", icon: FaIdCard, required: true },
  { name: "address", label: "Full Address", icon: FaMapMarkerAlt, as: "textarea", rows: 3 }
];

// Table columns configuration
const tableColumns = [
  {
    key: 'exName',
    label: 'College Name',
    sortable: true,
    render: (college) => (
      <div>
        <div className="fw-bold">{college.exName}</div>
        <div className="text-muted small">{college.exEmail || 'No email'}</div>
      </div>
    )
  },
  { key: 'exPlace', label: 'Place', sortable: true },
  {
    key: 'exIdentifier',
    label: 'Identifier',
    sortable: true,
    render: (college) => (
      <Badge bg="info-subtle" text="info-emphasis" className="font-monospace">
        {college.exIdentifier}
      </Badge>
    )
  }
];

// CSV export headers
const csvHeaders = [
  { key: 'exName', label: 'Name' },
  { key: 'exEmail', label: 'Email' },
  { key: 'exTelephone', label: 'Telephone' },
  { key: 'exPlace', label: 'Place' },
  { key: 'exIdentifier', label: 'Identifier' },
  { key: 'exIsActive', label: 'Status' }
];

// Transform form data to API payload
const transformPayload = (form, editId, userId) => ({
  exName: form.name.trim(),
  exEmail: form.email.trim(),
  exTelephone: form.telephone.trim(),
  exFax: form.fax.trim(),
  exPlace: form.place.trim(),
  exAddress: form.address.trim(),
  exIdentifier: form.identifier.trim(),
  exIsActive: form.isActive,
  user: form.user.trim(),
  exLocalLangName: form.localLangName.trim(),
  ...(editId && { id: editId })
});

// Transform API data to form
const transformToForm = (college) => ({
  name: college.exName || "",
  email: college.exEmail || "",
  telephone: college.exTelephone || "",
  fax: college.exFax || "",
  place: college.exPlace || "",
  address: college.exAddress || "",
  identifier: college.exIdentifier || "",
  isActive: college.exIsActive,
  user: college.user || "",
  localLangName: college.exLocalLangName || ""
});

// Get view modal fields
const getViewFields = (college) => {
  if (!college) return [];
  return [
    { label: "College Name", value: college.exName, icon: FaUniversity },
    { label: "Email", value: college.exEmail, icon: FaEnvelope },
    { label: "Telephone", value: college.exTelephone, icon: FaPhone },
    { label: "Fax", value: college.exFax, icon: FaPhone },
    { label: "Place", value: college.exPlace, icon: FaMapMarkerAlt },
    { label: "Identifier", value: college.exIdentifier, icon: FaIdCard },
    { label: "Local Language Name", value: college.exLocalLangName, icon: FaGlobe },
    { label: "Address", value: college.exAddress, icon: FaMapMarkerAlt, fullWidth: true }
  ];
};

export default function CollegePage() {
  return (
    <GenericCrudPage
      title="Colleges"
      config={collegeConfig}
      formFields={formFields}
      tableColumns={tableColumns}
      emptyIcon={FaUniversity}
      searchPlaceholder="Search by name, email, place, identifier..."
      csvHeaders={csvHeaders}
      csvFilename="colleges_export.csv"
      transformPayload={transformPayload}
      transformToForm={transformToForm}
      getViewFields={getViewFields}
    />
  );
}